import React, { useState, useEffect, useRef } from 'react';
import { CardContent, CircularProgress, Box, Stack } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import BuildIcon from '@mui/icons-material/Build';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import TimerIcon from '@mui/icons-material/Timer';
import PublicIcon from '@mui/icons-material/Public';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import {
  StyledCard,
  HeaderBox,
  HeaderIcon,
  HeaderTitle,
  StyledDivider,
  InfoItemBox,
  LabelBox,
  LabelIcon,
  LabelText,
  ValueText,
  UptimeChip,
} from './BuildInfo.styles';

interface BuildInfoData {
  name: string;
  version: string;
  environment: string;
  status: string;
  buildDate: string;
  startedAt: string;
  uptime: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    totalSeconds: number;
  };
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' 
    ? 'https://localhost:7292'
    : 'https://api.dotnethub.pl'
  );

const BuildInfo: React.FC = () => {
  const [buildInfo, setBuildInfo] = useState<BuildInfoData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentUptime, setCurrentUptime] = useState<number>(0);
  const startTimeRef = useRef<Date | null>(null);

  useEffect(() => {
    const fetchBuildInfo = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/`, {
          headers: { 'Accept': 'application/json' }
        });

        if (response.ok) {
          const data: BuildInfoData = await response.json();
          setBuildInfo(data);
          setCurrentUptime(data.uptime.totalSeconds);
          startTimeRef.current = new Date(data.startedAt);
        }
      } catch (error) {
        console.error('Failed to fetch build info:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBuildInfo();
  }, []);

  useEffect(() => {
    if (!buildInfo || !startTimeRef.current) return;

    let animationFrameId: number;
    
    const updateUptime = () => {
      if (startTimeRef.current) {
        const now = new Date();
        const diff = Math.floor((now.getTime() - startTimeRef.current.getTime()) / 1000);
        setCurrentUptime(diff);
      }
      animationFrameId = requestAnimationFrame(updateUptime);
    };

    animationFrameId = requestAnimationFrame(updateUptime);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [buildInfo]);

  const formatUptime = (seconds: number): string => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    const parts: string[] = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (mins > 0) parts.push(`${mins}m`);
    parts.push(`${secs}s`);

    return parts.join(' ');
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <StyledCard>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={2}>
            <CircularProgress size={30} sx={{ color: 'white' }} />
          </Box>
        </CardContent>
      </StyledCard>
    );
  }

  if (!buildInfo) {
    return null;
  }

  return (
    <StyledCard>
      <CardContent>
        <HeaderBox>
          <HeaderIcon>
            <InfoIcon sx={{ fontSize: 28 }} />
          </HeaderIcon>
          <HeaderTitle variant="h6">
            System Info
          </HeaderTitle>
        </HeaderBox>

        <StyledDivider />

        <Stack spacing={1.5}>
          <InfoItemBox>
            <LabelBox>
              <LabelIcon>
                <BuildIcon sx={{ fontSize: 20 }} />
              </LabelIcon>
              <LabelText variant="body2">
                Build Date
              </LabelText>
            </LabelBox>
            <ValueText variant="body2">
              {formatDate(buildInfo.buildDate)}
            </ValueText>
          </InfoItemBox>

          <InfoItemBox>
            <LabelBox>
              <LabelIcon>
                <RocketLaunchIcon sx={{ fontSize: 20 }} />
              </LabelIcon>
              <LabelText variant="body2">
                Started At
              </LabelText>
            </LabelBox>
            <ValueText variant="body2">
              {formatDate(buildInfo.startedAt)}
            </ValueText>
          </InfoItemBox>

          <InfoItemBox>
            <LabelBox>
              <LabelIcon>
                <TimerIcon sx={{ fontSize: 20 }} />
              </LabelIcon>
              <LabelText variant="body2">
                Uptime
              </LabelText>
            </LabelBox>
            <UptimeChip
              label={formatUptime(currentUptime)}
              size="small"
            />
          </InfoItemBox>

          <InfoItemBox>
            <LabelBox>
              <LabelIcon>
                <PublicIcon sx={{ fontSize: 20 }} />
              </LabelIcon>
              <LabelText variant="body2">
                Environment
              </LabelText>
            </LabelBox>
            <ValueText variant="body2">
              {buildInfo.environment}
            </ValueText>
          </InfoItemBox>

          <InfoItemBox>
            <LabelBox>
              <LabelIcon>
                <LocalOfferIcon sx={{ fontSize: 20 }} />
              </LabelIcon>
              <LabelText variant="body2">
                Version
              </LabelText>
            </LabelBox>
            <ValueText variant="body2">
              {buildInfo.version}
            </ValueText>
          </InfoItemBox>
        </Stack>
      </CardContent>
    </StyledCard>
  );
};

export default BuildInfo;
